import { useEffect } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useForm, useWatch } from "react-hook-form";

import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormColorPicker } from "@/components/Form/FormColorPicker";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormRadio, FormRadioGroup } from "@/components/Form/FormRadio";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Column, Columns } from "@/design-system/Columns";
import { FormField } from "@/design-system/FormField";
import { Info } from "@/design-system/Info";
import { Inline } from "@/design-system/Inline";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import { Tiles } from "@/design-system/Tiles";
import { canManageProjectManagement } from "@/helpers/permissions";
import { createImageSizeRule, createImageTypeRule } from "@/helpers/rules";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { usePermission } from "@/hooks/usePermission";
import { useTranslation } from "@/translations";
import { RuntimeBackgroundImage } from "@/ui-adapters/RuntimeBackgroundImage";

export interface ProjectStylingContentProps {
  defaultValues?: Partial<FormValues>;
  isSubmitting: boolean;
  onSubmit: (data: FormValues) => void;
}

export function ProjectStylingContent({
  defaultValues,
  isSubmitting,
  onSubmit,
}: ProjectStylingContentProps): React.ReactNode {
  const formMethods = useForm<FormValues>({ defaultValues });
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const hasPermission = usePermission();

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.project-styling.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: sessionUser.project.name,
            },
            {
              name: t("page.project-styling.title"),
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={formMethods} onSubmit={onSubmit}>
        <SectionCard>
          <EditForm isSubmitting={isSubmitting} formMethods={formMethods} />
          {!!hasPermission(canManageProjectManagement) && (
            <Form.Footer
              renderPrimaryAction={() => (
                <Button type="submit" isLoading={isSubmitting}>
                  {t("common.action.save")}
                </Button>
              )}
            />
          )}
        </SectionCard>
      </Form.Root>
    </DocumentPage>
  );
}

function EditForm({ formMethods }: { isSubmitting: boolean; formMethods: UseFormReturn<FormValues> }) {
  const { t } = useTranslation();
  const {
    selectedSplashScreenLogo,
    splashScreenBackground,
    useBackgroundImageForSplashScreen,
    background,
    logoLight,
    logoDark,
  } = useWatch<FormValues>();
  const hasPermission = usePermission();

  useEffect(() => {
    if (
      (!background?.length || (splashScreenBackground && splashScreenBackground.length > 0)) &&
      useBackgroundImageForSplashScreen
    ) {
      formMethods.setValue("useBackgroundImageForSplashScreen", false);
    }
  }, [background, splashScreenBackground, useBackgroundImageForSplashScreen, formMethods]);

  useEffect(() => {
    if (
      !logoLight ||
      (!logoLight.length && selectedSplashScreenLogo === "light") ||
      !logoDark ||
      (!logoDark.length && selectedSplashScreenLogo === "dark")
    ) {
      formMethods.setValue("selectedSplashScreenLogo", "none");
    }
  }, [logoLight, logoDark, selectedSplashScreenLogo, formMethods]);

  return (
    <>
      <section className="flex flex-col justify-start gap-space-sm pb-space-xl whitespace-pre-line @md:flex-row">
        <FormField label={t("page.project-styling.form.logos")} tooltip={t("page.project-styling.form.logos.tooltip")}>
          <Stack gap="md">
            <Inline gap="md">
              <FormField
                label={t("page.project-styling.form.logo-dark")}
                tooltip={t("page.project-styling.form.logo-dark.tooltip")}
              >
                <FormImageInput<FormValues, "logoDark">
                  name="logoDark"
                  rules={{
                    required: t("components.form.error.required", {
                      inputName: t("page.project-styling.form.logo-dark"),
                    }),
                    validate: {
                      size: createImageSizeRule({ t }),
                      type: createImageTypeRule({ t }),
                    },
                  }}
                  disabled={!hasPermission(canManageProjectManagement)}
                />
              </FormField>
              <FormField
                label={t("page.project-styling.form.logo-light")}
                tooltip={t("page.project-styling.form.logo-light.tooltip")}
              >
                <FormImageInput<FormValues, "logoLight">
                  name="logoLight"
                  rules={{
                    required: t("components.form.error.required", {
                      inputName: t("page.project-styling.form.logo-light"),
                    }),
                    validate: {
                      size: createImageSizeRule({ t }),
                      type: createImageTypeRule({ t }),
                    },
                  }}
                  disabled={!hasPermission(canManageProjectManagement)}
                />
              </FormField>
            </Inline>
            <FormRadioGroup layout="vertical">
              <FormRadio<FormValues, "selectedSplashScreenLogo">
                name="selectedSplashScreenLogo"
                value="dark"
                label={t("page.project-styling.form.splash-screen.logo-dark")}
                disabled={!logoDark || !logoDark.length || !hasPermission(canManageProjectManagement)}
              />
              <FormRadio<FormValues, "selectedSplashScreenLogo">
                name="selectedSplashScreenLogo"
                value="light"
                label={t("page.project-styling.form.splash-screen.logo-light")}
                disabled={!logoLight || !logoLight.length || !hasPermission(canManageProjectManagement)}
              />
              <FormRadio<FormValues, "selectedSplashScreenLogo">
                name="selectedSplashScreenLogo"
                value="none"
                label={t("page.project-styling.form.splash-screen.no-logo")}
                disabled={!hasPermission(canManageProjectManagement)}
              />
            </FormRadioGroup>
          </Stack>
        </FormField>
      </section>
      <Tiles columns={{ base: 1, container: { md: 2 } }} gap="lg">
        <Form.Body>
          <FormField
            label={t("page.project-styling.form.background")}
            tooltip={t("page.project-styling.form.background.tooltip")}
          >
            <FormImageInput<FormValues, "background">
              name="background"
              rules={{
                validate: {
                  size: createImageSizeRule({ t }),
                  type: createImageTypeRule({ t }),
                },
              }}
              disabled={!hasPermission(canManageProjectManagement)}
            />
            <Box padding={{ top: "sm" }}>
              <Inline gap="sm" align="center">
                <FormCheckbox
                  name="useBackgroundImageForSplashScreen"
                  label={t("page.project-styling.form.splash-screen.background")}
                  disabled={
                    !background?.length ||
                    (splashScreenBackground && splashScreenBackground.length > 0) ||
                    !hasPermission(canManageProjectManagement)
                  }
                />
                <Info tooltip={t("page.project-styling.form.splash-screen.background.tooltip")} />
              </Inline>
            </Box>
          </FormField>
          <FormField
            required
            label={t("page.project-styling.form.color1")}
            tooltip={t("page.project-styling.form.color1.tooltip")}
          >
            <FormColorPicker name="color1" disabled={!hasPermission(canManageProjectManagement)} />
          </FormField>
          <FormField
            required
            label={t("page.project-styling.form.color2")}
            tooltip={t("page.project-styling.form.color2.tooltip")}
          >
            <FormColorPicker name="color2" disabled={!hasPermission(canManageProjectManagement)} />
          </FormField>
          <FormField
            required
            label={t("page.project-styling.form.page-title-unscrolled-color")}
            tooltip={t("page.project-styling.form.page-title-unscrolled-color.tooltip")}
          >
            <FormColorPicker name="pageTitleUnscrolledColor" disabled={!hasPermission(canManageProjectManagement)} />
          </FormField>
          <FormField
            required
            label={t("page.project-styling.form.background-color")}
            tooltip={t("page.project-styling.form.background-color.tooltip")}
          >
            <FormColorPicker name="backgroundColor" disabled={!hasPermission(canManageProjectManagement)} />
          </FormField>
          <FormField
            required
            label={t("page.project-styling.form.color3")}
            tooltip={t("page.project-styling.form.color3.tooltip")}
          >
            <FormColorPicker name="color3" disabled={!hasPermission(canManageProjectManagement)} />
          </FormField>
        </Form.Body>
        <Columns gap="sm" collapseBelow="md">
          <Column>
            <Stack gap="sm">
              <Inline gap="sm" align="center">
                <span className="text-body-emphasis">{t("page.project-styling.splash-screen.preview")}</span>
                <Info tooltip={t("page.project-styling.splash-screen.preview.tooltip")} />
              </Inline>
              <article className="relative h-[417px] w-[208px] rounded-lg bg-background-default-inverse bg-cover bg-center shadow-lg">
                <RuntimeBackgroundImage
                  imageUrl={
                    splashScreenBackground?.[0]?.url ||
                    (useBackgroundImageForSplashScreen && background?.length ? background[0]?.url || "" : "")
                  }
                  presentation="transparentFill"
                >
                  {selectedSplashScreenLogo !== "none" && (
                    <span className="mx-auto mt-space-xl block w-illustration-compact">
                      <img
                        src={selectedSplashScreenLogo === "light" ? logoLight?.[0]?.url : logoDark?.[0]?.url}
                        // Decorative: the logo only illustrates the splash-screen preview next to its own label.
                        alt=""
                      />
                    </span>
                  )}
                </RuntimeBackgroundImage>
              </article>
            </Stack>
          </Column>
          <Column>
            {/* @md:items-start responsive alignment isn't representable by Stack's static align prop yet - left as raw Tailwind */}
            <section className="flex flex-col gap-space-sm pt-space-xl @md:items-start">
              <Inline gap="sm" align="start" wrap={false}>
                <FormImageInput<FormValues, "splashScreenBackground">
                  name="splashScreenBackground"
                  texts={{ add: t("page.project-styling.form.splash-screen.custom-background") }}
                  rules={{
                    validate: {
                      size: createImageSizeRule({ t }),
                      type: createImageTypeRule({ t }),
                    },
                  }}
                  disabled={!hasPermission(canManageProjectManagement)}
                />
                <Info
                  tooltip={t("page.project-styling.form.splash-screen.custom-background.tooltip")}
                  width="compact"
                />
              </Inline>
              <span className="max-w-xs text-overline whitespace-pre-line text-text-secondary">
                {t("page.project-styling.form.splash-screen.custom-background.tip")}
              </span>
            </section>
          </Column>
        </Columns>
      </Tiles>
    </>
  );
}

interface FormValues {
  logoDark: FormImage[];
  logoLight: FormImage[];
  background: FormImage[];
  splashScreenBackground: FormImage[];
  useBackgroundImageForSplashScreen: boolean;
  selectedSplashScreenLogo: "light" | "dark" | "none";
  color1: string;
  color2: string;
  color3: string;
  backgroundColor: string;
  pageTitleUnscrolledColor: string;
}
